/**
 * Railway Calculation Service
 *
 * Handles all calculation logic for Railway projects based on Excel formulas.
 * This service encapsulates the complex mathematical formulations used in railway
 * pavement design, including moduli calculations, Poisson ratios, and geometric parameters.
 */

import { IRailwayData } from '../api/IApiResponse';

/**
 * Interface for Railway calculation results
 */
export interface IRailwayCalculationResult {
    // Experimental coefficients
    fbc: number; // Fixed experimental coefficient for ballast/subballast (kPa)
    fsg: number; // Fixed experimental coefficient for subgrade (kPa)
    fc: number; // Fixed experimental coefficient for cohesion

    // Moduli (MPa)
    eb: number; // Modulus of ballast
    esb: number; // Modulus of subballast
    esg: number; // Modulus of subgrade

    // Ratios
    ebEsg: number; // Ratio Eb/Esg
    ebEsb: number; // Ratio Eb/Esb

    // Load spreading coefficients
    chi1: number; // Coefficient χ1
    chi2: number; // Coefficient χ2

    // Undrained cohesion
    cu: number; // Undrained cohesion of subgrade (kPa)

    // Geometric calculations
    equivalentLength: number; // Equivalent length of loaded area on top of ballast (m)
    widthLoadedArea: number; // Width of loaded area on top of ballast (m)
    loadedAreaBallast: number; // Loaded area on ballast (m²)
    equivalentSquareSize: number; // Equivalent square size (m)

    // Train-specific calculations
    trainCalculations: IRailwayTrainCalculations;

    // Advanced calculations (rows 41-65)
    unstabilizedCalculations: IRailwayUnstabilizedCalculations;
    stabilizedCalculations: IRailwayStabilizedCalculations;

    // Design summary (final results - rows 73-78)
    designSummary: IRailwayDesignSummary;
}

/**
 * Interface for Railway Design Summary (final results)
 */
export interface IRailwayDesignSummary {
    unstabilized: {
        ballastThicknessWithoutSubballast: number; // hu (m) - C73
        ballastVerticalDeformationWithoutSubballast: number; // dvu (m) - C74
        ballastThicknessWithSubballastBelow: number; // h'bu (m) - C75
        subballastThickness: number; // hsbu (m) - C76
        ballastVerticalDeformationWithSubballastBelow: number; // d'vu (m) - C77
        rutDepthAtBallastSubgradeInterface: number; // ru (m) - C78
    };
    stabilized: {
        ballastThicknessWithoutSubballast: number; // hs (m) - F73
        ballastVerticalDeformationWithoutSubballast: number; // dvs (m) - F74
        ballastThicknessWithSubballastBelow: number; // h'bs (m) - F75
        subballastThickness: number; // hsbs (m) - F76
        ballastVerticalDeformationWithSubballastBelow: number; // d'vs (m) - F77
        rutDepthAtBallastSubgradeInterface: number; // rs (m) - F78
    };
}

/**
 * Interface for train-specific calculations (Cargo and Passenger)
 */
export interface IRailwayTrainCalculations {
    // Cargo Train calculations
    cargo: {
        wheelLoad: number; // Pc (kN)
        dynamicAmplificationFactor: number; // φ (-)
        dynamicWheelLoad: number; // Pdyn (kN)
        uniformPressureOnBallast: number; // σsb (kPa)
        totalNumberOfWheelPassages: number; // N
        plasticStrainUnstabilised: number; // εbu (%)
        plasticStrainStabilised: number; // εbs (%)
    };

    // Passenger Train calculations
    passenger: {
        wheelLoad: number; // Pp (kN)
        dynamicAmplificationFactor: number; // φ (-)
        dynamicWheelLoad: number; // Pdyn (kN)
        uniformPressureOnBallast: number; // σsb (kPa)
        totalNumberOfWheelPassages: number; // N
        plasticStrainUnstabilised: number; // εbu (%)
        plasticStrainStabilised: number; // εbs (%)
    };

    // Combined/Maximum values
    maxDynamicWheelLoad: number; // Pdyn, max (kN)
    maximumUniformPressureOnBallast: number; // σsb, max (kPa)
    totalNumberOfWheelPassagesTotal: number; // Ntot
    totalPlasticStrainUnstabilised: number; // εbu, tot (%)
    totalPlasticStrainStabilised: number; // εbs, tot (%)
}

/**
 * Interface for unstabilized ballast calculations (rows 41-65, column C)
 */
export interface IRailwayUnstabilizedCalculations {
    ballastThickness: number; // hbu (m) - C41 (goal seek result)
    equivalentBallastThickness: number; // heu (m) - C42
    equivalentBallastThicknessWithSubballast: number; // hgu (m) - C43
    boussinesqR1: number; // R1 (-) - C44
    boussinesqR2: number; // R2 (-) - C45
    boussinesqR3: number; // R3 (-) - C46
    stressRatio: number; // σsb/σbs (-) - C47
    tanLoadSpreadingAngleFirst: number; // tan αb1u (-) - C48
    coefficientK2: number; // K2u (-) - C49
    coefficientLambda2: number; // λ2u (-) - C50
    tanLoadSpreadingAngleAfterN: number; // tan αbNu (-) - C51
    bearingCapacityFactor: number; // Ncu (-) - C52
    bearingCapacity: number; // qcu (kPa) - C53
    ballastSubgradeContactArea: number; // Absu (m²) - C54
    pressureOnSubgrade: number; // σbsu (kPa) - C55
    pressureBearingCapacityRatio: number; // (σbsu/qcu) (-) - C56
    cumulativePlasticStrain: number; // εspu (-) - C57
    rutDepth: number; // ru (m) - C58
    criticalRutDepth: number; // rcr (m) - C59
    coefficientM: number; // mu (-) - C60
    mobilizedBearingCapacity: number; // qcmu (kPa) - C61
    pdynMaxCalculated: number; // Pdynu, max (kN) - C62
    pdynDifference: number; // Pdyn - Pdyn calculated (-) - C63
    ballastThicknessWithSubballast: number; // h'bu (m) - C64
    subballastThickness: number; // h'sbu (m) - C65
}

/**
 * Interface for stabilized ballast calculations (rows 41-65, column F)
 */
export interface IRailwayStabilizedCalculations {
    ballastThickness: number; // hbs (m) - F41 (goal seek result)
    equivalentBallastThickness: number; // hes (m) - F42
    equivalentBallastThicknessWithSubballast: number; // hgs (m) - F43
    boussinesqR1: number; // R1 (-) - F44
    boussinesqR2: number; // R2 (-) - F45
    boussinesqR3: number; // R3 (-) - F46
    stressRatio: number; // σsb/σbs (-) - F47
    tanLoadSpreadingAngleFirst: number; // tan αb1s (-) - F48
    coefficientK2: number; // K2s (-) - F49
    coefficientLambda2: number; // λ2s (-) - F50
    tanLoadSpreadingAngleAfterN: number; // tan αbNs (-) - F51
    bearingCapacityFactor: number; // Ncs (-) - F52
    bearingCapacity: number; // qcs (kPa) - F53
    ballastSubgradeContactArea: number; // Abss (m²) - F54
    pressureOnSubgrade: number; // σbss (kPa) - F55
    pressureBearingCapacityRatio: number; // (σbss/qcs) (-) - F56
    cumulativePlasticStrain: number; // εsps (-) - F57
    rutDepth: number; // rs (m) - F58
    criticalRutDepth: number; // rcr (m) - F59
    coefficientM: number; // ms (-) - F60
    mobilizedBearingCapacity: number; // qcms (kPa) - F61
    pdynMaxCalculated: number; // Pdyns, max (kN) - F62
    pdynDifference: number; // Pdyn - Pdyn calculated (-) - F63
    ballastThicknessWithSubballast: number; // h'bs (m) - F64
    subballastThickness: number; // h'sbs (m) - F65
}

/**
 * Interface for Railway input parameters extracted from form data
 */
export interface IRailwayInputParameters {
    // CBR values (%)
    cbrOfBallast: number;
    cbrOfSubballast: number;
    cbrOfSubgrade: number;

    // Poisson ratios
    poissonRatioOfBallast: number;
    poissonRatioOfSubballast: number;
    poissonRatioOfSubgrade: number;

    // Geometric parameters
    railWidth: number; // Rail width (m)
    sleeperThickness: number; // Sleeper thickness (m)
    sleeperWidth: number; // Sleeper width (m)
    loadSpreadingAngle: number; // Load spreading angle in sleepers (degrees)
    thicknessOfSoftSubgrade: number; // Thickness of soft subgrade (m)

    // Train configuration
    selectedTrainType: string; // "CARGO TRAIN", "PASSENGER TRAIN", or "MIXED TRAIN"

    // Cargo train parameters
    cargoDesignAxleLoad: number; // kN
    cargoTrainSpeed: number; // km/h
    cargoWheelDiameter: number; // m
    cargoNumberOfTrainsPerDay: number;
    cargoNumberOfCarsPerTrain: number;
    cargoNumberOfAxlePerCar: number;
    cargoDesignLife: number; // years

    // Passenger train parameters
    passengerDesignAxleLoad: number; // kN
    passengerTrainSpeed: number; // km/h
    passengerWheelDiameter: number; // m
    passengerNumberOfTrainsPerDay: number;
    passengerNumberOfCarsPerTrain: number;
    passengerNumberOfAxlePerCar: number;
    passengerDesignLife: number; // years

    // Ballast type for plastic strain calculation
    ballastType: string; // "Basalt", "Granite", or "Dolomite"

    // Subgrade soil type
    subgradeSoilType: string; // "Fat Clay", "Lean Clay", "Elastic Silt", "Silt"

    // Geogrid parameters
    geogridProduct: string; // Selected geogrid product
    geogridStrength: number; // T 2% (kN/m)

    // Advanced design parameters
    setBallastThicknessWithSubballast: number; // Set ballast thickness with subballast below (m)
    setPercentReductionBallastThickness: number; // Set percent reduction of ballast thickness (%)
}

/**
 * Railway Calculation Service
 *
 * This service handles all railway-specific calculations following the exact Excel formulas
 * used in the original spreadsheet implementation.
 */
export class RailwayCalculationService {
    /**
     * Fixed experimental coefficients based on Excel specifications
     */
    private static readonly EXPERIMENTAL_COEFFICIENTS = {
        FBC: 36.0, // Fixed experimental coefficient for ballast/subballast (kPa)
        FSG: 10.0, // Fixed experimental coefficient for subgrade (kPa)
        FC: 30.0, // Fixed experimental coefficient for cohesion
    };

    /**
     * Default fallback values for input parameters
     */
    private static readonly DEFAULT_VALUES = {
        CBR_BALLAST: 45, // % - Default CBR of ballast
        CBR_SUBBALLAST: 30, // % - Default CBR of subballast
        CBR_SUBGRADE: 5, // % - Default CBR of subgrade
        POISSON_BALLAST: 0.35, // Default Poisson ratio of ballast
        POISSON_SUBBALLAST: 0.42, // Default Poisson ratio of subballast
        POISSON_SUBGRADE: 0.3, // Default Poisson ratio of subgrade
        RAIL_WIDTH: 0.16, // m - Default rail width
        SLEEPER_THICKNESS: 0.221, // m - Default sleeper thickness
        SLEEPER_WIDTH: 0.25, // m - Default sleeper width
        LOAD_SPREADING_ANGLE: 60, // degrees - Default load spreading angle
        THICKNESS_SOFT_SUBGRADE: 5, // m - Default thickness of soft subgrade

        // Train configuration defaults
        SELECTED_TRAIN_TYPE: 'CARGO TRAIN',
        CARGO_DESIGN_AXLE_LOAD: 500, // kN
        CARGO_TRAIN_SPEED: 60, // km/h
        CARGO_WHEEL_DIAMETER: 0.915, // m
        CARGO_TRAINS_PER_DAY: 50,
        CARGO_CARS_PER_TRAIN: 10,
        CARGO_AXLE_PER_CAR: 4,
        CARGO_DESIGN_LIFE: 20, // years

        PASSENGER_DESIGN_AXLE_LOAD: 170, // kN
        PASSENGER_TRAIN_SPEED: 300, // km/h
        PASSENGER_WHEEL_DIAMETER: 0.9, // m
        PASSENGER_TRAINS_PER_DAY: 1,
        PASSENGER_CARS_PER_TRAIN: 10,
        PASSENGER_AXLE_PER_CAR: 4,
        PASSENGER_DESIGN_LIFE: 20, // years

        BALLAST_TYPE: 'Granite',
        SUBGRADE_SOIL_TYPE: 'Lean Clay',
        GEOGRID_PRODUCT: 'MacGrid EG 30S',
        GEOGRID_STRENGTH: 11, // kN/m - T 2%
        SET_BALLAST_THICKNESS_WITH_SUBBALLAST: 0.2, // m
        SET_PERCENT_REDUCTION_BALLAST_THICKNESS: 0, // %
    };

    /**
     * Map form ballast type values to calculation service expected values
     */
    private static mapBallastTypeToCalculationValue(formValue: string): string {
        const mapping: { [key: string]: string } = {
            granite: 'Granite',
            basalt: 'Basalt',
            limestone: 'Dolomite', // limestone maps to Dolomite in calculations
            dolomite: 'Dolomite',
        };

        const mappedValue = mapping[formValue?.toLowerCase()];
        if (!mappedValue) {
            return 'Granite';
        }

        return mappedValue;
    }

    /**
     * Map form soil type values to calculation service expected values
     */
    private static mapSoilTypeToCalculationValue(formValue: string): string {
        const mapping: { [key: string]: string } = {
            lean_clay: 'Lean Clay',
            fat_clay: 'Fat Clay',
            silty_clay: 'Elastic Silt', // silty_clay maps to Elastic Silt in calculations
            sandy_clay: 'Silt', // sandy_clay maps to Silt in calculations
            elastic_silt: 'Elastic Silt',
            silt: 'Silt',
        };

        const mappedValue = mapping[formValue?.toLowerCase()];
        if (!mappedValue) {
            return 'Lean Clay';
        }

        return mappedValue;
    }

    /**
     * Resolve a load-input field (axle load / train speed / wheel diameter) to a valid positive
     * number, falling back to the default otherwise.
     *
     * These fields require a strictly positive value (ProjectTypeService min:0, BE requires >0):
     * 0, '' and NaN are INVALID and must NOT propagate — wheelDiameter is a divisor in the dynamic
     * amplification factor (calculateDynamicAmplificationFactor), so a 0 would yield Infinity/NaN.
     * This is why a plain `??` is unsafe here (it lets 0/'' through) and `||` alone misclassifies
     * nothing valid (no valid value is falsy) but is implicit — this helper makes the rule explicit.
     */
    private static positiveNumberOrDefault(value: unknown, defaultValue: number): number {
        const numeric = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : defaultValue;
    }

    /**
     * Extract input parameters from Railway data structure
     *
     * @param railwayData - The railway project data
     * @returns Extracted and validated input parameters
     */
    static extractInputParameters(railwayData: IRailwayData): IRailwayInputParameters {
        // 🔧 FIX: Railway now uses FLAT structure (no .default wrapper) - unified with other projects
        // BEFORE: const formData = (railwayData as any)?.default;
        // After refactor 7de2a790, Railway data is stored FLAT like Paved/Airport/Unpaved
        const formData = railwayData as any;

        // Debug log to see the structure of incoming data

        // Extract CBR values from soilProperties section
        const soilProps = formData?.soilProperties;
        const cbrOfBallast = soilProps?.cbrOfBallast || this.DEFAULT_VALUES.CBR_BALLAST;
        const cbrOfSubballast = soilProps?.cbrOfSubballast || this.DEFAULT_VALUES.CBR_SUBBALLAST;
        const cbrOfSubgrade = soilProps?.cbrOfSubgrade || this.DEFAULT_VALUES.CBR_SUBGRADE;

        // Warn if using defaults for soil properties
        if (!soilProps?.cbrOfBallast) undefined;
        if (!soilProps?.cbrOfSubballast) undefined;
        if (!soilProps?.cbrOfSubgrade) undefined;

        // Extract Poisson ratios from poissonRatios section
        const poissonRatios = formData?.poissonRatios;
        const poissonRatioOfBallast = poissonRatios?.poissonRatioOfBallast || this.DEFAULT_VALUES.POISSON_BALLAST;
        const poissonRatioOfSubballast = poissonRatios?.poissonRatioOfSubballast || this.DEFAULT_VALUES.POISSON_SUBBALLAST;
        const poissonRatioOfSubgrade = poissonRatios?.poissonRatioOfSubgrade || this.DEFAULT_VALUES.POISSON_SUBGRADE;

        // Warn if using defaults for Poisson ratios
        if (!poissonRatios?.poissonRatioOfBallast) undefined;
        if (!poissonRatios?.poissonRatioOfSubballast) undefined;
        if (!poissonRatios?.poissonRatioOfSubgrade) undefined;

        // Extract geometric parameters from trackGeometry section
        const trackGeom = formData?.trackGeometry;
        const railWidth = trackGeom?.railWidth || this.DEFAULT_VALUES.RAIL_WIDTH;
        const sleeperThickness = trackGeom?.sleeperThickness || this.DEFAULT_VALUES.SLEEPER_THICKNESS;
        const sleeperWidth = trackGeom?.sleeperWidth || this.DEFAULT_VALUES.SLEEPER_WIDTH;
        const loadSpreadingAngle = trackGeom?.loadSpreadingAngle || this.DEFAULT_VALUES.LOAD_SPREADING_ANGLE;

        // Warn if using defaults for track geometry
        if (!trackGeom?.railWidth) undefined;
        if (!trackGeom?.sleeperThickness) undefined;
        if (!trackGeom?.sleeperWidth) undefined;
        if (!trackGeom?.loadSpreadingAngle) undefined;

        // Extract train configuration and selected train type
        const railwayTypeConfig = formData?.railwayType;
        const selectedTrainType = railwayTypeConfig?.railwayToDesign || this.DEFAULT_VALUES.SELECTED_TRAIN_TYPE;

        // Convert selectedTrainType to consistent format for calculations
        const normalizedTrainType =
            selectedTrainType === 'cargo_train' ? 'CARGO TRAIN' : selectedTrainType === 'passenger_train' ? 'PASSENGER TRAIN' : selectedTrainType === 'mixed_train' ? 'MIXED TRAIN' : selectedTrainType;

        // Warn if using default value
        if (!railwayTypeConfig?.railwayToDesign) {
        }

        // Extract load data (design axle load, train speed, wheel diameter)
        const loadData = formData?.loadData;
        const designAxleLoad = loadData?.designAxleLoad;
        const trainSpeed = loadData?.trainSpeed;
        const wheelDiameter = loadData?.wheelDiameter;

        // Extract traffic data (trains per day, cars per train, etc.)
        const trafficData = formData?.trafficData;
        const numberOfTrainsPerDay = trafficData?.numberOfTrainsPerDay;
        const numberOfCarsPerTrain = trafficData?.numberOfCarsPerTrain;
        const numberOfAxisPerCar = trafficData?.numberOfAxisPerCar;
        const designLife = trafficData?.designLife;

        // Determine train type and apply defaults based on selected type
        let cargoDesignAxleLoad, cargoTrainSpeed, cargoWheelDiameter;
        let cargoNumberOfTrainsPerDay, cargoNumberOfCarsPerTrain, cargoNumberOfAxlePerCar, cargoDesignLife;
        let passengerDesignAxleLoad, passengerTrainSpeed, passengerWheelDiameter;
        let passengerNumberOfTrainsPerDay, passengerNumberOfCarsPerTrain, passengerNumberOfAxlePerCar, passengerDesignLife;

        if (selectedTrainType === 'cargo_train' || selectedTrainType === 'CARGO TRAIN') {
            // Use current form values for cargo, defaults for passenger
            cargoDesignAxleLoad = this.positiveNumberOrDefault(designAxleLoad, this.DEFAULT_VALUES.CARGO_DESIGN_AXLE_LOAD);
            cargoTrainSpeed = this.positiveNumberOrDefault(trainSpeed, this.DEFAULT_VALUES.CARGO_TRAIN_SPEED);
            cargoWheelDiameter = this.positiveNumberOrDefault(wheelDiameter, this.DEFAULT_VALUES.CARGO_WHEEL_DIAMETER);
            cargoNumberOfTrainsPerDay = numberOfTrainsPerDay || this.DEFAULT_VALUES.CARGO_TRAINS_PER_DAY;
            cargoNumberOfCarsPerTrain = numberOfCarsPerTrain || this.DEFAULT_VALUES.CARGO_CARS_PER_TRAIN;
            cargoNumberOfAxlePerCar = numberOfAxisPerCar || this.DEFAULT_VALUES.CARGO_AXLE_PER_CAR;
            cargoDesignLife = designLife || this.DEFAULT_VALUES.CARGO_DESIGN_LIFE;

            // Use passenger defaults
            passengerDesignAxleLoad = this.DEFAULT_VALUES.PASSENGER_DESIGN_AXLE_LOAD;
            passengerTrainSpeed = this.DEFAULT_VALUES.PASSENGER_TRAIN_SPEED;
            passengerWheelDiameter = this.DEFAULT_VALUES.PASSENGER_WHEEL_DIAMETER;
            passengerNumberOfTrainsPerDay = this.DEFAULT_VALUES.PASSENGER_TRAINS_PER_DAY;
            passengerNumberOfCarsPerTrain = this.DEFAULT_VALUES.PASSENGER_CARS_PER_TRAIN;
            passengerNumberOfAxlePerCar = this.DEFAULT_VALUES.PASSENGER_AXLE_PER_CAR;
            passengerDesignLife = this.DEFAULT_VALUES.PASSENGER_DESIGN_LIFE;

            // Warn about defaults used
            if (!designAxleLoad) undefined;
            if (!trainSpeed) undefined;
            if (!wheelDiameter) undefined;
            if (!numberOfTrainsPerDay) undefined;
            if (!numberOfCarsPerTrain) undefined;
            if (!numberOfAxisPerCar) undefined;
            if (!designLife) undefined;
        } else if (selectedTrainType === 'passenger_train' || selectedTrainType === 'PASSENGER TRAIN') {
            // Use current form values for passenger, defaults for cargo
            passengerDesignAxleLoad = this.positiveNumberOrDefault(designAxleLoad, this.DEFAULT_VALUES.PASSENGER_DESIGN_AXLE_LOAD);
            passengerTrainSpeed = this.positiveNumberOrDefault(trainSpeed, this.DEFAULT_VALUES.PASSENGER_TRAIN_SPEED);
            passengerWheelDiameter = this.positiveNumberOrDefault(wheelDiameter, this.DEFAULT_VALUES.PASSENGER_WHEEL_DIAMETER);
            passengerNumberOfTrainsPerDay = numberOfTrainsPerDay || this.DEFAULT_VALUES.PASSENGER_TRAINS_PER_DAY;
            passengerNumberOfCarsPerTrain = numberOfCarsPerTrain || this.DEFAULT_VALUES.PASSENGER_CARS_PER_TRAIN;
            passengerNumberOfAxlePerCar = numberOfAxisPerCar || this.DEFAULT_VALUES.PASSENGER_AXLE_PER_CAR;
            passengerDesignLife = designLife || this.DEFAULT_VALUES.PASSENGER_DESIGN_LIFE;

            // Use cargo defaults
            cargoDesignAxleLoad = this.DEFAULT_VALUES.CARGO_DESIGN_AXLE_LOAD;
            cargoTrainSpeed = this.DEFAULT_VALUES.CARGO_TRAIN_SPEED;
            cargoWheelDiameter = this.DEFAULT_VALUES.CARGO_WHEEL_DIAMETER;
            cargoNumberOfTrainsPerDay = this.DEFAULT_VALUES.CARGO_TRAINS_PER_DAY;
            cargoNumberOfCarsPerTrain = this.DEFAULT_VALUES.CARGO_CARS_PER_TRAIN;
            cargoNumberOfAxlePerCar = this.DEFAULT_VALUES.CARGO_AXLE_PER_CAR;
            cargoDesignLife = this.DEFAULT_VALUES.CARGO_DESIGN_LIFE;

            // Warn about defaults used
            if (!designAxleLoad) undefined;
            if (!trainSpeed) undefined;
            if (!wheelDiameter) undefined;
            if (!numberOfTrainsPerDay) undefined;
            if (!numberOfCarsPerTrain) undefined;
            if (!numberOfAxisPerCar) undefined;
            if (!designLife) undefined;
        } else {
            // Mixed train - use separate dedicated fields for cargo and passenger
            if (selectedTrainType === 'mixed_train' || selectedTrainType === 'MIXED TRAIN') {
                // Extract cargo-specific fields from loadData section
                cargoDesignAxleLoad = this.positiveNumberOrDefault(loadData?.designAxleLoadCargo, this.DEFAULT_VALUES.CARGO_DESIGN_AXLE_LOAD);
                cargoTrainSpeed = this.positiveNumberOrDefault(loadData?.trainSpeedCargo, this.DEFAULT_VALUES.CARGO_TRAIN_SPEED);
                cargoWheelDiameter = this.positiveNumberOrDefault(loadData?.wheelDiameterCargo, this.DEFAULT_VALUES.CARGO_WHEEL_DIAMETER);

                // Extract cargo-specific fields from trafficData section
                cargoNumberOfTrainsPerDay = trafficData?.numberOfTrainsPerDayCargo || this.DEFAULT_VALUES.CARGO_TRAINS_PER_DAY;
                cargoNumberOfCarsPerTrain = trafficData?.numberOfCarsPerTrainCargo || this.DEFAULT_VALUES.CARGO_CARS_PER_TRAIN;
                cargoNumberOfAxlePerCar = trafficData?.numberOfAxisPerCarCargo || this.DEFAULT_VALUES.CARGO_AXLE_PER_CAR;
                cargoDesignLife = trafficData?.designLifeCargo || this.DEFAULT_VALUES.CARGO_DESIGN_LIFE;

                // Extract passenger-specific fields from loadData section
                passengerDesignAxleLoad = this.positiveNumberOrDefault(loadData?.designAxleLoadPassenger, this.DEFAULT_VALUES.PASSENGER_DESIGN_AXLE_LOAD);
                passengerTrainSpeed = this.positiveNumberOrDefault(loadData?.trainSpeedPassenger, this.DEFAULT_VALUES.PASSENGER_TRAIN_SPEED);
                passengerWheelDiameter = this.positiveNumberOrDefault(loadData?.wheelDiameterPassenger, this.DEFAULT_VALUES.PASSENGER_WHEEL_DIAMETER);

                // Extract passenger-specific fields from trafficData section
                passengerNumberOfTrainsPerDay = trafficData?.numberOfTrainsPerDayPassenger || this.DEFAULT_VALUES.PASSENGER_TRAINS_PER_DAY;
                passengerNumberOfCarsPerTrain = trafficData?.numberOfCarsPerTrainPassenger || this.DEFAULT_VALUES.PASSENGER_CARS_PER_TRAIN;
                passengerNumberOfAxlePerCar = trafficData?.numberOfAxisPerCarPassenger || this.DEFAULT_VALUES.PASSENGER_AXLE_PER_CAR;
                passengerDesignLife = trafficData?.designLifePassenger || this.DEFAULT_VALUES.PASSENGER_DESIGN_LIFE;
            } else {
                // Unknown train type - use current form values, fallback to cargo defaults
                cargoDesignAxleLoad = this.positiveNumberOrDefault(designAxleLoad, this.DEFAULT_VALUES.CARGO_DESIGN_AXLE_LOAD);
                cargoTrainSpeed = this.positiveNumberOrDefault(trainSpeed, this.DEFAULT_VALUES.CARGO_TRAIN_SPEED);
                cargoWheelDiameter = this.positiveNumberOrDefault(wheelDiameter, this.DEFAULT_VALUES.CARGO_WHEEL_DIAMETER);
                cargoNumberOfTrainsPerDay = numberOfTrainsPerDay || this.DEFAULT_VALUES.CARGO_TRAINS_PER_DAY;
                cargoNumberOfCarsPerTrain = numberOfCarsPerTrain || this.DEFAULT_VALUES.CARGO_CARS_PER_TRAIN;
                cargoNumberOfAxlePerCar = numberOfAxisPerCar || this.DEFAULT_VALUES.CARGO_AXLE_PER_CAR;
                cargoDesignLife = designLife || this.DEFAULT_VALUES.CARGO_DESIGN_LIFE;

                // For unknown train type, passenger values are also the same as form values or defaults
                passengerDesignAxleLoad = this.positiveNumberOrDefault(designAxleLoad, this.DEFAULT_VALUES.PASSENGER_DESIGN_AXLE_LOAD);
                passengerTrainSpeed = this.positiveNumberOrDefault(trainSpeed, this.DEFAULT_VALUES.PASSENGER_TRAIN_SPEED);
                passengerWheelDiameter = this.positiveNumberOrDefault(wheelDiameter, this.DEFAULT_VALUES.PASSENGER_WHEEL_DIAMETER);
                passengerNumberOfTrainsPerDay = numberOfTrainsPerDay || this.DEFAULT_VALUES.PASSENGER_TRAINS_PER_DAY;
                passengerNumberOfCarsPerTrain = numberOfCarsPerTrain || this.DEFAULT_VALUES.PASSENGER_CARS_PER_TRAIN;
                passengerNumberOfAxlePerCar = numberOfAxisPerCar || this.DEFAULT_VALUES.PASSENGER_AXLE_PER_CAR;
                passengerDesignLife = designLife || this.DEFAULT_VALUES.PASSENGER_DESIGN_LIFE;
            }
        }

        // Extract ballast type from materialTypes section
        const materialTypes = formData?.materialTypes;
        const rawBallastType = materialTypes?.ballastType || formData?.ballastType || soilProps?.ballastType || this.DEFAULT_VALUES.BALLAST_TYPE;
        const ballastType = this.mapBallastTypeToCalculationValue(rawBallastType);

        // Extract additional parameters
        const soilProperties = formData?.soilProperties;
        const thicknessOfSoftSubgrade =
            soilProperties?.thicknessOfSoftSubgrade || formData?.thicknessOfSoftSubgrade || trackGeom?.thicknessOfSoftSubgrade || this.DEFAULT_VALUES.THICKNESS_SOFT_SUBGRADE;
        const rawSubgradeSoilType = materialTypes?.subgradeSoilType || formData?.subgradeSoilType || soilProps?.subgradeSoilType || this.DEFAULT_VALUES.SUBGRADE_SOIL_TYPE;
        const subgradeSoilType = this.mapSoilTypeToCalculationValue(rawSubgradeSoilType);

        // Extract geogrid parameters
        const geoGrid = formData?.geoGrid;
        const geogridProduct = geoGrid?.selectedGeogrid || formData?.geogridProduct || this.DEFAULT_VALUES.GEOGRID_PRODUCT;
        const geogridStrength = geoGrid?.tensileStrengthAt2Percent || formData?.geogridStrength || this.DEFAULT_VALUES.GEOGRID_STRENGTH;

        // Extract design parameters
        const designParameters = formData?.designParameters;
        const setBallastThicknessWithSubballast =
            designParameters?.setStabilisedBallastThickness || formData?.setBallastThicknessWithSubballast || this.DEFAULT_VALUES.SET_BALLAST_THICKNESS_WITH_SUBBALLAST;
        const setPercentReductionBallastThickness =
            designParameters?.setPercentReduction || formData?.setPercentReductionBallastThickness || this.DEFAULT_VALUES.SET_PERCENT_REDUCTION_BALLAST_THICKNESS;

        // Warn if using defaults for additional parameters
        if (!materialTypes?.ballastType && !formData?.ballastType && !soilProps?.ballastType) {
        }
        if (!soilProperties?.thicknessOfSoftSubgrade && !formData?.thicknessOfSoftSubgrade && !trackGeom?.thicknessOfSoftSubgrade) {
        }
        if (!materialTypes?.subgradeSoilType && !formData?.subgradeSoilType && !soilProps?.subgradeSoilType) {
        }
        if (!geoGrid?.selectedGeogrid && !formData?.geogridProduct) {
        }
        if (!geoGrid?.tensileStrengthAt2Percent && !formData?.geogridStrength) {
        }
        if (!designParameters?.setStabilisedBallastThickness && !formData?.setBallastThicknessWithSubballast) {
        }
        if (!designParameters?.setPercentReduction && !formData?.setPercentReductionBallastThickness) {
        }

        return {
            cbrOfBallast,
            cbrOfSubballast,
            cbrOfSubgrade,
            poissonRatioOfBallast,
            poissonRatioOfSubballast,
            poissonRatioOfSubgrade,
            railWidth,
            sleeperThickness,
            sleeperWidth,
            loadSpreadingAngle,
            thicknessOfSoftSubgrade,
            selectedTrainType: normalizedTrainType, // Use normalized train type for calculations
            cargoDesignAxleLoad,
            cargoTrainSpeed,
            cargoWheelDiameter,
            cargoNumberOfTrainsPerDay,
            cargoNumberOfCarsPerTrain,
            cargoNumberOfAxlePerCar,
            cargoDesignLife,
            passengerDesignAxleLoad,
            passengerTrainSpeed,
            passengerWheelDiameter,
            passengerNumberOfTrainsPerDay,
            passengerNumberOfCarsPerTrain,
            passengerNumberOfAxlePerCar,
            passengerDesignLife,
            ballastType,
            subgradeSoilType,
            geogridProduct,
            geogridStrength,
            setBallastThicknessWithSubballast,
            setPercentReductionBallastThickness,
        };
    }

    /**
     * Calculate moduli based on Excel formulas
     *
     * @param params - Input parameters
     * @returns Object containing calculated moduli
     */
    static calculateModuli(params: IRailwayInputParameters) {
        const { FBC, FSG } = this.EXPERIMENTAL_COEFFICIENTS;
        const { cbrOfBallast, cbrOfSubballast, cbrOfSubgrade } = params;

        // Calculate moduli based on Excel formulas
        const Eb = FBC * Math.pow(cbrOfBallast, 0.3); // Modulus of ballast (MPa)
        const Esb = FBC * Math.pow(cbrOfSubballast, 0.3); // Modulus of subballast (MPa)
        const Esg = FSG * cbrOfSubgrade; // Modulus of subgrade (MPa)

        return { Eb, Esb, Esg };
    }

    /**
     * Calculate load spreading coefficients χ1 and χ2 based on exact Excel formulas
     *
     * @param params - Input parameters
     * @param moduli - Calculated moduli
     * @returns Object containing coefficients χ1 and χ2
     */
    static calculateLoadSpreadingCoefficients(params: IRailwayInputParameters, moduli: { Eb: number; Esb: number; Esg: number }) {
        const { poissonRatioOfBallast, poissonRatioOfSubballast, poissonRatioOfSubgrade } = params;
        const { Eb, Esb, Esg } = moduli;

        // Calculate squared Poisson ratios
        const poissonBallastSq = Math.pow(poissonRatioOfBallast, 2);
        const poissonSubBallastSq = Math.pow(poissonRatioOfSubballast, 2);
        const poissonSubgradeSq = Math.pow(poissonRatioOfSubgrade, 2);

        // Calculate coefficients based on exact Excel formulas
        const chi1 = Math.pow((Eb * (1 - poissonSubgradeSq)) / (Esg * (1 - poissonBallastSq)), 1 / 3);
        const chi2 = Math.pow(((Esb / Esg) * (1 - poissonSubgradeSq)) / (1 - poissonSubBallastSq), 1 / 3);

        return { chi1, chi2 };
    }

    /**
     * Calculate geometric parameters for loaded area analysis
     *
     * @param params - Input parameters
     * @returns Object containing geometric calculations
     */
    static calculateGeometricParameters(params: IRailwayInputParameters) {
        const { railWidth, sleeperThickness, sleeperWidth, loadSpreadingAngle } = params;

        // Convert load spreading angle to radians for calculation
        const loadSpreadingAngleRad = loadSpreadingAngle * (Math.PI / 180);

        // Calculate equivalent length of loaded area on top of ballast
        const equivalentLength = railWidth + 2 * sleeperThickness * Math.tan(loadSpreadingAngleRad);

        // Width of loaded area on top of ballast equals sleeper width
        const widthLoadedArea = sleeperWidth;

        // Calculate loaded area on ballast
        const loadedAreaBallast = equivalentLength * sleeperWidth;

        // Calculate equivalent square size
        const equivalentSquareSize = Math.pow(loadedAreaBallast, 0.5);

        return {
            equivalentLength,
            widthLoadedArea,
            loadedAreaBallast,
            equivalentSquareSize,
        };
    }

    /**
     * Main calculation method that orchestrates all railway calculations
     *
     * @param railwayData - The railway project data
     * @returns Complete calculation results
     */
    /**
     * Calculate all railway parameters, allowing optional initial guesses for Ballast thickness (unstabilized and stabilized)
     * @param railwayData - Input data
     * @param initialGuessUnstabilized - Optional initial guess for unstabilized Ballast thickness (meters)
     * @param initialGuessStabilized - Optional initial guess for stabilized Ballast thickness (meters)
     */
    static calculateRailwayParameters(railwayData: IRailwayData, initialGuessUnstabilized?: number, initialGuessStabilized?: number): IRailwayCalculationResult {
        // Extract input parameters from data structure
        const params = this.extractInputParameters(railwayData);

        // Calculate moduli
        const { Eb, Esb, Esg } = this.calculateModuli(params);

        // Calculate ratios
        const EbEsg = Eb / Esg; // Ratio Eb/Esg
        const EbEsb = Eb / Esb; // Ratio Eb/Esb

        // Calculate load spreading coefficients
        const { chi1, chi2 } = this.calculateLoadSpreadingCoefficients(params, { Eb, Esb, Esg });

        // Calculate undrained cohesion of subgrade
        const cu = this.EXPERIMENTAL_COEFFICIENTS.FC * params.cbrOfSubgrade;

        // Calculate geometric parameters
        const { equivalentLength, widthLoadedArea, loadedAreaBallast, equivalentSquareSize } = this.calculateGeometricParameters(params);

        // Prepare first table values to pass to train-specific calculations
        const firstTableValues = {
            loadedAreaBallast,
            Eb,
            Esb,
            Esg,
            EbEsg,
            EbEsb,
            chi1,
            chi2,
            cu,
            equivalentLength,
            widthLoadedArea,
            equivalentSquareSize,
        };

        // Calculate advanced ballast design calculations
        const unstabilizedCalculations = this.calculateUnstabilizedBallastDesign(params, firstTableValues, initialGuessUnstabilized);
        const stabilizedCalculations = this.calculateStabilizedBallastDesign(params, firstTableValues, initialGuessStabilized);

        // Return results with appropriate rounding
        return {
            // Experimental coefficients
            fbc: this.EXPERIMENTAL_COEFFICIENTS.FBC,
            fsg: this.EXPERIMENTAL_COEFFICIENTS.FSG,
            fc: this.EXPERIMENTAL_COEFFICIENTS.FC,

            // Moduli (MPa) - rounded to 1 decimal
            eb: Math.round(Eb * 10) / 10,
            esb: Math.round(Esb * 10) / 10,
            esg: Math.round(Esg * 10) / 10,

            // Ratios - rounded to 2 decimals
            ebEsg: Math.round(EbEsg * 100) / 100,
            ebEsb: Math.round(EbEsb * 100) / 100,

            // Coefficients - rounded to 3 decimals
            chi1: Math.round(chi1 * 1000) / 1000,
            chi2: Math.round(chi2 * 1000) / 1000,

            // Undrained cohesion - rounded to 1 decimal (kPa)
            cu: Math.round(cu * 10) / 10,

            // Geometric calculations with appropriate precision
            equivalentLength: Math.round(equivalentLength * 1000) / 1000, // 3 decimals (m)
            widthLoadedArea: Math.round(widthLoadedArea * 1000) / 1000, // 3 decimals (m)
            loadedAreaBallast: Math.round(loadedAreaBallast * 10000) / 10000, // 4 decimals (m²)
            equivalentSquareSize: Math.round(equivalentSquareSize * 1000) / 1000, // 3 decimals (m)

            // Train-specific calculations
            trainCalculations: this.calculateTrainSpecificData(params, firstTableValues),

            // Advanced calculations
            unstabilizedCalculations,
            stabilizedCalculations,

            // Design summary (final results)
            designSummary: this.calculateDesignSummary(unstabilizedCalculations, stabilizedCalculations, this.calculateTrainSpecificData(params, firstTableValues)),
        };
    }

    /**
     * Calculate train-specific data including cargo and passenger calculations
     *
     * @param params - Input parameters
     * @param loadedAreaBallast - Loaded area on ballast (m²)
     * @returns Train-specific calculation results
     */
    /**
     * Calculate train-specific data using already-calculated 'first table' values
     *
     * @param params - Input parameters
     * @param firstTableValues - Object containing all relevant first table values
     * @returns Train-specific calculation results
     */
    private static calculateTrainSpecificData(
        params: IRailwayInputParameters,
        firstTableValues: {
            loadedAreaBallast: number;
            Eb: number;
            Esb: number;
            Esg: number;
            EbEsg: number;
            EbEsb: number;
            chi1: number;
            chi2: number;
            cu: number;
            equivalentLength: number;
            widthLoadedArea: number;
            equivalentSquareSize: number;
        }
    ): IRailwayTrainCalculations {
        // Use loadedAreaBallast from first table for all relevant calculations
        const { loadedAreaBallast } = firstTableValues;

        // Calculate cargo train values
        const cargoWheelLoad = this.calculateWheelLoad(params.selectedTrainType, 'CARGO', params.cargoDesignAxleLoad);
        const cargoDynamicAmplificationFactor = this.calculateDynamicAmplificationFactor(params.selectedTrainType, 'CARGO', params.cargoTrainSpeed, params.cargoWheelDiameter);
        const cargoDynamicWheelLoad = this.calculateDynamicWheelLoad(params.cargoNumberOfTrainsPerDay, cargoWheelLoad, cargoDynamicAmplificationFactor);
        // Use loadedAreaBallast from first table (not recalculated)
        const cargoUniformPressure = this.calculateUniformPressureOnBallast(cargoDynamicWheelLoad, loadedAreaBallast);
        const cargoTotalPassages = this.calculateTotalWheelPassages(
            params.selectedTrainType,
            'CARGO',
            params.cargoNumberOfTrainsPerDay,
            params.cargoNumberOfCarsPerTrain,
            params.cargoNumberOfAxlePerCar,
            params.cargoDesignLife
        );
        const cargoPlasticStrainUnstabilised = this.calculatePlasticStrainUnstabilised(params.cargoNumberOfTrainsPerDay, params.ballastType, cargoTotalPassages);
        const cargoPlasticStrainStabilised = this.calculatePlasticStrainStabilised(cargoPlasticStrainUnstabilised, cargoDynamicWheelLoad);

        // Calculate passenger train values
        const passengerWheelLoad = this.calculateWheelLoad(params.selectedTrainType, 'PASSENGER', params.passengerDesignAxleLoad);
        const passengerDynamicAmplificationFactor = this.calculateDynamicAmplificationFactor(params.selectedTrainType, 'PASSENGER', params.passengerTrainSpeed, params.passengerWheelDiameter);
        const passengerDynamicWheelLoad = this.calculateDynamicWheelLoad(params.passengerNumberOfTrainsPerDay, passengerWheelLoad, passengerDynamicAmplificationFactor);
        // Use loadedAreaBallast from first table (not recalculated)
        const passengerUniformPressure = this.calculateUniformPressureOnBallast(passengerDynamicWheelLoad, loadedAreaBallast);
        const passengerTotalPassages = this.calculateTotalWheelPassages(
            params.selectedTrainType,
            'PASSENGER',
            params.passengerNumberOfTrainsPerDay,
            params.passengerNumberOfCarsPerTrain,
            params.passengerNumberOfAxlePerCar,
            params.passengerDesignLife
        );
        const passengerPlasticStrainUnstabilised = this.calculatePlasticStrainUnstabilised(params.passengerNumberOfTrainsPerDay, params.ballastType, passengerTotalPassages);
        const passengerPlasticStrainStabilised = this.calculatePlasticStrainStabilised(passengerPlasticStrainUnstabilised, passengerDynamicWheelLoad);

        // Calculate combined/maximum values
        const maxDynamicWheelLoad = Math.max(cargoDynamicWheelLoad, passengerDynamicWheelLoad);
        const maximumUniformPressureOnBallast = Math.max(cargoUniformPressure, passengerUniformPressure);
        const totalNumberOfWheelPassagesTotal = cargoTotalPassages + passengerTotalPassages;
        const totalPlasticStrainUnstabilised =
            cargoTotalPassages === 0
                ? passengerPlasticStrainUnstabilised
                : passengerTotalPassages === 0
                  ? cargoPlasticStrainUnstabilised
                  : cargoPlasticStrainUnstabilised + passengerPlasticStrainUnstabilised;
        const totalPlasticStrainStabilised =
            cargoTotalPassages === 0 ? passengerPlasticStrainStabilised : passengerTotalPassages === 0 ? cargoPlasticStrainStabilised : cargoPlasticStrainStabilised + passengerPlasticStrainStabilised;

        return {
            cargo: {
                wheelLoad: Math.round(cargoWheelLoad * 10) / 10,
                dynamicAmplificationFactor: Math.round(cargoDynamicAmplificationFactor * 1000) / 1000,
                dynamicWheelLoad: Math.round(cargoDynamicWheelLoad * 10) / 10,
                uniformPressureOnBallast: Math.round(cargoUniformPressure),
                totalNumberOfWheelPassages: cargoTotalPassages,
                plasticStrainUnstabilised: Math.round(cargoPlasticStrainUnstabilised * 1000) / 1000,
                plasticStrainStabilised: Math.round(cargoPlasticStrainStabilised * 1000) / 1000,
            },
            passenger: {
                wheelLoad: Math.round(passengerWheelLoad * 10) / 10,
                dynamicAmplificationFactor: Math.round(passengerDynamicAmplificationFactor * 1000) / 1000,
                dynamicWheelLoad: Math.round(passengerDynamicWheelLoad * 10) / 10,
                uniformPressureOnBallast: Math.round(passengerUniformPressure),
                totalNumberOfWheelPassages: passengerTotalPassages,
                plasticStrainUnstabilised: Math.round(passengerPlasticStrainUnstabilised * 1000) / 1000,
                plasticStrainStabilised: Math.round(passengerPlasticStrainStabilised * 1000) / 1000,
            },
            maxDynamicWheelLoad: Math.round(maxDynamicWheelLoad * 10) / 10,
            maximumUniformPressureOnBallast: Math.round(maximumUniformPressureOnBallast),
            totalNumberOfWheelPassagesTotal: totalNumberOfWheelPassagesTotal,
            totalPlasticStrainUnstabilised: Math.round(totalPlasticStrainUnstabilised * 1000) / 1000,
            totalPlasticStrainStabilised: Math.round(totalPlasticStrainStabilised * 1000) / 1000,
        };
    }

    /**
     * Calculate wheel load based on Excel formula
     * Excel: =IF(INPUT_RAILWAY!C7=INPUT_RAILWAY!C9,INPUT_RAILWAY!C11/4,IF(INPUT_RAILWAY!C7="MIXED TRAIN",INPUT_RAILWAY!C11/4,0))
     */
    private static calculateWheelLoad(selectedTrainType: string, trainType: 'CARGO' | 'PASSENGER', designAxleLoad: number): number {
        if (selectedTrainType === 'CARGO TRAIN' && trainType === 'CARGO') {
            return designAxleLoad / 4;
        }
        if (selectedTrainType === 'PASSENGER TRAIN' && trainType === 'PASSENGER') {
            return designAxleLoad / 4;
        }
        if (selectedTrainType === 'MIXED TRAIN') {
            return designAxleLoad / 4;
        }
        return 0;
    }

    /**
     * Calculate dynamic amplification factor
     * Excel: =IF(INPUT_RAILWAY!C7=INPUT_RAILWAY!C9,1+0.0052*INPUT_RAILWAY!C12/INPUT_RAILWAY!C13,IF(INPUT_RAILWAY!C7="MIXED TRAIN",1+0.0052*INPUT_RAILWAY!C12/INPUT_RAILWAY!C13,0))
     */
    private static calculateDynamicAmplificationFactor(selectedTrainType: string, trainType: 'CARGO' | 'PASSENGER', trainSpeed: number, wheelDiameter: number): number {
        if (selectedTrainType === 'CARGO TRAIN' && trainType === 'CARGO') {
            return 1 + (0.0052 * trainSpeed) / wheelDiameter;
        }
        if (selectedTrainType === 'PASSENGER TRAIN' && trainType === 'PASSENGER') {
            return 1 + (0.0052 * trainSpeed) / wheelDiameter;
        }
        if (selectedTrainType === 'MIXED TRAIN') {
            return 1 + (0.0052 * trainSpeed) / wheelDiameter;
        }
        return 0;
    }

    /**
     * Calculate dynamic wheel load
     * Excel: =IF(INPUT_RAILWAY!C18=0,0,C26*C27)
     */
    private static calculateDynamicWheelLoad(numberOfTrainsPerDay: number, wheelLoad: number, dynamicAmplificationFactor: number): number {
        if (numberOfTrainsPerDay === 0) {
            return 0;
        }
        return wheelLoad * dynamicAmplificationFactor;
    }

    /**
     * Calculate uniform pressure on ballast
     * Excel: =C28/$C$21
     */
    private static calculateUniformPressureOnBallast(dynamicWheelLoad: number, loadedAreaBallast: number): number {
        return dynamicWheelLoad / loadedAreaBallast;
    }

    /**
     * Calculate total number of wheel passages
     * Excel: =IF(INPUT_RAILWAY!C7=INPUT_RAILWAY!C9,INPUT_RAILWAY!C18*INPUT_RAILWAY!C19*INPUT_RAILWAY!C20*INPUT_RAILWAY!C21*365,IF(INPUT_RAILWAY!C7="MIXED TRAIN",INPUT_RAILWAY!C18*INPUT_RAILWAY!C19*INPUT_RAILWAY!C20*INPUT_RAILWAY!C21*365,0))
     */
    private static calculateTotalWheelPassages(
        selectedTrainType: string,
        trainType: 'CARGO' | 'PASSENGER',
        trainsPerDay: number,
        carsPerTrain: number,
        axlePerCar: number,
        designLife: number
    ): number {
        if (selectedTrainType === 'CARGO TRAIN' && trainType === 'CARGO') {
            return trainsPerDay * carsPerTrain * axlePerCar * designLife * 365;
        }
        if (selectedTrainType === 'PASSENGER TRAIN' && trainType === 'PASSENGER') {
            return trainsPerDay * carsPerTrain * axlePerCar * designLife * 365;
        }
        if (selectedTrainType === 'MIXED TRAIN') {
            return trainsPerDay * carsPerTrain * axlePerCar * designLife * 365;
        }
        return 0;
    }

    /**
     * Calculate plastic strain of unstabilised ballast
     * Excel: =IF(INPUT_RAILWAY!C18=0,0,IF(INPUT_RAILWAY!$C$29="Basalt",(4.82*(0.3^1.42)*(1+(LN(C32))^0.49)),IF(INPUT_RAILWAY!$C$29="Granite",(1.27*(0.67^2.41)*(1+(LN(C32))^0.48)),IF(INPUT_RAILWAY!$C$29="Dolomite",(4.23*(0.4^1.15)*(1+(LN(C32))^0.32)),"Wrong ballast type"))))
     */
    private static calculatePlasticStrainUnstabilised(numberOfTrainsPerDay: number, ballastType: string, totalWheelPassages: number): number {
        if (numberOfTrainsPerDay === 0) {
            return 0;
        }

        if (totalWheelPassages <= 0) {
            return 0;
        }

        const lnPassages = Math.log(totalWheelPassages);

        switch (ballastType) {
            case 'Basalt':
                return 4.82 * Math.pow(0.3, 1.42) * (1 + Math.pow(lnPassages, 0.49));
            case 'Granite':
                return 1.27 * Math.pow(0.67, 2.41) * (1 + Math.pow(lnPassages, 0.48));
            case 'Dolomite':
                return 4.23 * Math.pow(0.4, 1.15) * (1 + Math.pow(lnPassages, 0.32));
            default:
                return 0;
        }
    }

    /**
     * Calculate plastic strain of stabilised ballast
     * Excel: =(C34*(0.05163*C28-0.4245)/(0.0655*C28-0.8256))
     */
    private static calculatePlasticStrainStabilised(plasticStrainUnstabilised: number, dynamicWheelLoad: number): number {
        if (plasticStrainUnstabilised === 0 || dynamicWheelLoad === 0) {
            return 0;
        }

        const numerator = 0.05163 * dynamicWheelLoad - 0.4245;
        const denominator = 0.0655 * dynamicWheelLoad - 0.8256;

        if (Math.abs(denominator) < 1e-3) {
            console.warn(`Railway: plastic strain denominator near-zero (${denominator.toFixed(6)}) at dynamicWheelLoad=${dynamicWheelLoad} kN, returning 0`);
            return 0;
        }

        return plasticStrainUnstabilised * (numerator / denominator);
    }

    /**
     * Utility method to validate Railway data structure
     *
     * @param railwayData - The railway project data to validate
     * @returns boolean indicating if data is valid for calculations
     */
    static isValidRailwayData(railwayData: any): railwayData is IRailwayData {
        return railwayData !== null && railwayData !== undefined && typeof railwayData === 'object';
    }

    /**
     * Goal Seek implementation - finds the value of a parameter that makes a target function equal to zero
     *
     * @param targetFunction - Function that takes a parameter value and returns the difference from target
     * @param initialGuess - Initial guess for the parameter value
     * @param tolerance - Tolerance for convergence (default: 1e-6)
     * @param maxIterations - Maximum number of iterations (default: 1000)
     * @returns The parameter value that makes the target function approximately zero
     */
    private static goalSeek(targetFunction: (x: number) => number, initialGuess: number, tolerance = 1e-6, maxIterations = 1000): number {
        let x = initialGuess;
        let iteration = 0;

        while (iteration < maxIterations) {
            const fx = targetFunction(x);

            // Check for convergence
            if (Math.abs(fx) < tolerance) {
                return x;
            }

            // Calculate derivative using finite difference (secant method)
            const h = Math.abs(x) * 1e-8 + 1e-8; // Small step
            const fxh = targetFunction(x + h);
            const derivative = (fxh - fx) / h;

            // Avoid division by zero
            if (Math.abs(derivative) < 1e-12) {
                // Try a different direction
                x += fx > 0 ? -0.01 : 0.01;
            } else {
                // Newton-Raphson step
                x = x - fx / derivative;
            }

            // Keep x in reasonable bounds
            x = Math.max(0.01, Math.min(x, 10.0));

            iteration++;
        }

        // Warn if result is at boundary — may indicate non-convergence
        const finalFx = targetFunction(x);
        if (Math.abs(finalFx) > tolerance * 100) {
            console.warn(`Railway goal-seek: non-convergence after ${maxIterations} iterations. x=${x.toFixed(4)}, f(x)=${finalFx.toFixed(6)}, tolerance=${tolerance}`);
        }

        return x;
    }

    /**
     * Calculate unstabilized ballast design parameters (rows 41-65, column C)
     *
     * @param params - Input parameters
     * @param firstTableValues - Already calculated first table values
     * @returns Unstabilized ballast calculations
     */
    /**
     * Calculate unstabilized ballast design parameters (rows 41-65, column C)
     * @param params - Input parameters
     * @param firstTableValues - Already calculated first table values
     * @param initialGuess - Optional initial guess for Ballast thickness (meters)
     */
    private static calculateUnstabilizedBallastDesign(
        params: IRailwayInputParameters,
        firstTableValues: {
            loadedAreaBallast: number;
            Eb: number;
            Esb: number;
            Esg: number;
            EbEsg: number;
            EbEsb: number;
            chi1: number;
            chi2: number;
            cu: number;
            equivalentLength: number;
            widthLoadedArea: number;
            equivalentSquareSize: number;
        },
        initialGuess?: number
    ): IRailwayUnstabilizedCalculations {
        // Goal Seek to find ballast thickness (C41) that makes C63 = 0
        // Note: For compatibility with Excel results, we use a hybrid approach
        // Excel goal seek converges to different values due to precision/tolerance differences
        const ballastThickness = this.goalSeek(
            (hbu: number) => {
                // Calculate the chain of values from C41 to C63
                const calculationChain = this.calculateUnstabilizedChain(hbu, params, firstTableValues);

                // Debug logging for goal seek iterations
                if (Math.abs(calculationChain.pdynDifference) > 1e-6) {
                }

                return calculationChain.pdynDifference; // This should be zero
            },
            initialGuess !== undefined ? initialGuess : 1.08, // Match Excel expected value
            1e-5 // Use looser tolerance to potentially match Excel behavior
        ); // Use provided initial guess or default 1.08m (closer to Excel)

        // Calculate final values with the found ballast thickness
        const finalCalculations = this.calculateUnstabilizedChain(ballastThickness, params, firstTableValues);

        return finalCalculations;
    }

    /**
     * Calculate stabilized ballast design parameters (rows 41-65, column F)
     *
     * @param params - Input parameters
     * @param firstTableValues - Already calculated first table values
     * @returns Stabilized ballast calculations
     */
    /**
     * Calculate stabilized ballast design parameters (rows 41-65, column F)
     * @param params - Input parameters
     * @param firstTableValues - Already calculated first table values
     * @param initialGuess - Optional initial guess for Ballast thickness (meters)
     */
    private static calculateStabilizedBallastDesign(
        params: IRailwayInputParameters,
        firstTableValues: {
            loadedAreaBallast: number;
            Eb: number;
            Esb: number;
            Esg: number;
            EbEsg: number;
            EbEsb: number;
            chi1: number;
            chi2: number;
            cu: number;
            equivalentLength: number;
            widthLoadedArea: number;
            equivalentSquareSize: number;
        },
        initialGuess?: number
    ): IRailwayStabilizedCalculations {
        // Goal Seek to find ballast thickness (F41) that makes F63 = 0
        const ballastThickness = this.goalSeek(
            (hbs: number) => {
                // Calculate the chain of values from F41 to F63
                const calculationChain = this.calculateStabilizedChain(hbs, params, firstTableValues);
                return calculationChain.pdynDifference; // This should be zero
            },
            initialGuess !== undefined ? initialGuess : 0.6
        ); // Use provided initial guess or default 0.6m

        // Calculate final values with the found ballast thickness
        const finalCalculations = this.calculateStabilizedChain(ballastThickness, params, firstTableValues);

        return finalCalculations;
    }

    /**
     * Calculate the complete chain of unstabilized calculations from C41 to C63
     *
     * @param ballastThickness - The ballast thickness (C41)
     * @param params - Input parameters
     * @param firstTableValues - First table values
     * @returns Complete unstabilized calculations
     */
    private static calculateUnstabilizedChain(
        ballastThickness: number,
        params: IRailwayInputParameters,
        firstTableValues: {
            loadedAreaBallast: number;
            Eb: number;
            Esb: number;
            Esg: number;
            EbEsg: number;
            EbEsb: number;
            chi1: number;
            chi2: number;
            cu: number;
            equivalentLength: number;
            widthLoadedArea: number;
            equivalentSquareSize: number;
        }
    ): IRailwayUnstabilizedCalculations {
        const { chi1, chi2, cu, equivalentLength, equivalentSquareSize } = firstTableValues;

        // C42: Equivalent ballast thickness (without subballast below)
        const equivalentBallastThickness = ballastThickness * chi1;

        // C43: Equivalent ballast thickness (with subballast below)
        const equivalentBallastThicknessWithSubballast = ballastThickness * chi2;

        // C44: Boussinesq R1
        const boussinesqR1 = Math.sqrt(equivalentLength ** 2 + equivalentBallastThickness ** 2);

        // C45: Boussinesq R2 (using INPUT_RAILWAY!C16 = sleeper width)
        const boussinesqR2 = Math.sqrt(params.sleeperWidth ** 2 + equivalentBallastThickness ** 2);

        // C46: Boussinesq R3
        const boussinesqR3 = Math.sqrt(equivalentLength ** 2 + params.sleeperWidth ** 2 + equivalentBallastThickness ** 2);

        // C47: Ratio of the vertical stress at top and bottom of ballast
        const denominator =
            Math.atan((equivalentLength * params.sleeperWidth) / (equivalentBallastThickness * boussinesqR3)) +
            ((equivalentLength * params.sleeperWidth * equivalentBallastThickness) / boussinesqR3) * (1 / boussinesqR1 ** 2 + 1 / boussinesqR2 ** 2);
        const stressRatio = 6.2832 / denominator;

        // C48: tan of load spreading angle at the first application
        const safeEquivThickness = Math.max(0.001, 2 * equivalentBallastThickness);
        const tanLoadSpreadingAngleFirst = (equivalentSquareSize / safeEquivThickness) * Math.sqrt(Math.max(0, stressRatio - 1));

        // C49: Coefficient K2u
        const coefficientK2 = (equivalentSquareSize / ballastThickness) ** 0.81 * 0.58;

        // Get total number of wheel passages from params (calculated earlier)
        const totalPassages = this.getTotalWheelPassages(params);

        // C50: Coefficient λ2u
        const coefficientLambda2 = 1 / (1 + coefficientK2 * Math.log(totalPassages));

        // C51: tan of load spreading angle after N load repetitions
        const tanLoadSpreadingAngleAfterN = coefficientLambda2 * tanLoadSpreadingAngleFirst;

        // C52: Bearing capacity factor (fixed value for unstabilized)
        const bearingCapacityFactor = 3.8;

        // C53: Bearing capacity
        const bearingCapacity = bearingCapacityFactor * cu;

        // C54: Ballast / subgrade contact area
        const ballastSubgradeContactArea =
            (equivalentLength + 2 * equivalentBallastThickness * tanLoadSpreadingAngleAfterN) * (params.sleeperWidth + 2 * equivalentBallastThickness * tanLoadSpreadingAngleAfterN);

        // Get max dynamic wheel load (C30)
        const maxDynamicWheelLoad = this.getMaxDynamicWheelLoad(params);

        // C55: Pressure on subgrade
        const pressureOnSubgrade = maxDynamicWheelLoad / ballastSubgradeContactArea;

        // C56: Pressure on subgrade / bearing capacity
        const pressureBearingCapacityRatio = pressureOnSubgrade / bearingCapacity;

        // C57: Cumulative plastic strain of subgrade
        const cumulativePlasticStrain = this.calculateCumulativePlasticStrain(params, totalPassages, pressureBearingCapacityRatio);

        // C58: Rut depth at the ballast – subgrade interface
        const rutDepth = cumulativePlasticStrain * params.thicknessOfSoftSubgrade;

        // C59: Critical rut depth
        const criticalRutDepth = 0.025 * (1.5 + 0.125 * Math.log(totalPassages));

        // C60: Coefficient m
        const coefficientM = ((1 - Math.exp(-0.78 * (equivalentSquareSize / ballastThickness))) * rutDepth) / criticalRutDepth;

        // C61: Mobilized bearing capacity
        const mobilizedBearingCapacity = coefficientM * bearingCapacity;

        // C62: Pdyn, max calculated
        const pdynMaxCalculated =
            coefficientM *
            bearingCapacityFactor *
            cu *
            ((2 * equivalentBallastThickness * tanLoadSpreadingAngleFirst) / (0.85 * (1 + coefficientK2 * Math.log(totalPassages))) + equivalentSquareSize) ** 2;

        // C63: Pdyn - Pdyn calculated (goal seek target)
        const pdynDifference = maxDynamicWheelLoad - pdynMaxCalculated;

        // C64: Ballast thickness with subballast below
        const ballastThicknessWithSubballast =
            params.setBallastThicknessWithSubballast > 0 ? params.setBallastThicknessWithSubballast : ((100 - params.setPercentReductionBallastThickness) * ballastThickness) / 100;

        // C65: Subballast thickness
        const subballastThickness = (equivalentBallastThickness - ballastThicknessWithSubballast * chi1) / chi2;

        return {
            ballastThickness: Math.round(ballastThickness * 10000) / 10000,
            equivalentBallastThickness: Math.round(equivalentBallastThickness * 10000) / 10000,
            equivalentBallastThicknessWithSubballast: Math.round(equivalentBallastThicknessWithSubballast * 10000) / 10000,
            boussinesqR1: Math.round(boussinesqR1 * 10000) / 10000,
            boussinesqR2: Math.round(boussinesqR2 * 10000) / 10000,
            boussinesqR3: Math.round(boussinesqR3 * 10000) / 10000,
            stressRatio: Math.round(stressRatio * 10000) / 10000,
            tanLoadSpreadingAngleFirst: Math.round(tanLoadSpreadingAngleFirst * 10000) / 10000,
            coefficientK2: Math.round(coefficientK2 * 10000) / 10000,
            coefficientLambda2: Math.round(coefficientLambda2 * 10000) / 10000,
            tanLoadSpreadingAngleAfterN: Math.round(tanLoadSpreadingAngleAfterN * 10000) / 10000,
            bearingCapacityFactor: bearingCapacityFactor,
            bearingCapacity: Math.round(bearingCapacity * 10) / 10,
            ballastSubgradeContactArea: Math.round(ballastSubgradeContactArea * 10000) / 10000,
            pressureOnSubgrade: Math.round(pressureOnSubgrade * 100) / 100,
            pressureBearingCapacityRatio: Math.round(pressureBearingCapacityRatio * 10000) / 10000,
            cumulativePlasticStrain: Math.round(cumulativePlasticStrain * 1000000) / 1000000,
            rutDepth: Math.round(rutDepth * 10000) / 10000,
            criticalRutDepth: Math.round(criticalRutDepth * 10000) / 10000,
            coefficientM: Math.round(coefficientM * 10000) / 10000,
            mobilizedBearingCapacity: Math.round(mobilizedBearingCapacity * 100) / 100,
            pdynMaxCalculated: Math.round(pdynMaxCalculated * 100) / 100,
            pdynDifference: Math.round(pdynDifference * 1000000) / 1000000,
            ballastThicknessWithSubballast: Math.round(ballastThicknessWithSubballast * 1000) / 1000,
            subballastThickness: Math.round(subballastThickness * 10000) / 10000,
        };
    }

    /**
     * Calculate the complete chain of stabilized calculations from F41 to F63
     *
     * @param ballastThickness - The ballast thickness (F41)
     * @param params - Input parameters
     * @param firstTableValues - First table values
     * @returns Complete stabilized calculations
     */
    private static calculateStabilizedChain(
        ballastThickness: number,
        params: IRailwayInputParameters,
        firstTableValues: {
            loadedAreaBallast: number;
            Eb: number;
            Esb: number;
            Esg: number;
            EbEsg: number;
            EbEsb: number;
            chi1: number;
            chi2: number;
            cu: number;
            equivalentLength: number;
            widthLoadedArea: number;
            equivalentSquareSize: number;
        }
    ): IRailwayStabilizedCalculations {
        const { chi1, chi2, cu, equivalentLength, equivalentSquareSize } = firstTableValues;

        // F42: Equivalent ballast thickness (without subballast below)
        const equivalentBallastThickness = ballastThickness * chi1;

        // F43: Equivalent ballast thickness (with subballast below)
        const equivalentBallastThicknessWithSubballast = ballastThickness * chi2;

        // F44: Boussinesq R1
        const boussinesqR1 = Math.sqrt(equivalentLength ** 2 + equivalentBallastThickness ** 2);

        // F45: Boussinesq R2
        const boussinesqR2 = Math.sqrt(params.sleeperWidth ** 2 + equivalentBallastThickness ** 2);

        // F46: Boussinesq R3
        const boussinesqR3 = Math.sqrt(equivalentLength ** 2 + params.sleeperWidth ** 2 + equivalentBallastThickness ** 2);

        // F47: Ratio of the vertical stress at top and bottom of ballast
        const denominator =
            Math.atan((equivalentLength * params.sleeperWidth) / (equivalentBallastThickness * boussinesqR3)) +
            ((equivalentLength * params.sleeperWidth * equivalentBallastThickness) / boussinesqR3) * (1 / boussinesqR1 ** 2 + 1 / boussinesqR2 ** 2);
        const stressRatio = 6.2832 / denominator;

        // F48: tan of load spreading angle at the first application
        const safeEquivThickness = Math.max(0.001, 2 * equivalentBallastThickness);
        const tanLoadSpreadingAngleFirst = (equivalentSquareSize / safeEquivThickness) * Math.sqrt(Math.max(0, stressRatio - 1));

        // F49: Coefficient K2s (different formula for stabilized case with geogrid strength)
        const geogridStrength = this.getGeogridStrength(params); // T 2% from params
        const coefficientK2 = (equivalentSquareSize / ballastThickness) ** 0.81 * Math.max(0.58 - 0.000046 * geogridStrength ** 4.5, 0.15);

        // Get total number of wheel passages from params
        const totalPassages = this.getTotalWheelPassages(params);

        // F50: Coefficient λ2s
        const coefficientLambda2 = 1 / (1 + coefficientK2 * Math.log(totalPassages));

        // F51: tan of load spreading angle after N load repetitions
        const tanLoadSpreadingAngleAfterN = coefficientLambda2 * tanLoadSpreadingAngleFirst;

        // F52: Bearing capacity factor (higher value for stabilized)
        const bearingCapacityFactor = 6.04;

        // F53: Bearing capacity
        const bearingCapacity = bearingCapacityFactor * cu;

        // F54: Ballast / subgrade contact area
        const ballastSubgradeContactArea =
            (equivalentLength + 2 * equivalentBallastThickness * tanLoadSpreadingAngleAfterN) * (params.sleeperWidth + 2 * equivalentBallastThickness * tanLoadSpreadingAngleAfterN);

        // Get max dynamic wheel load (C30)
        const maxDynamicWheelLoad = this.getMaxDynamicWheelLoad(params);

        // F55: Pressure on subgrade
        const pressureOnSubgrade = maxDynamicWheelLoad / ballastSubgradeContactArea;

        // F56: Pressure on subgrade / bearing capacity
        const pressureBearingCapacityRatio = pressureOnSubgrade / bearingCapacity;

        // F57: Cumulative plastic strain of subgrade
        const cumulativePlasticStrain = this.calculateCumulativePlasticStrain(params, totalPassages, pressureBearingCapacityRatio);

        // F58: Rut depth at the ballast – subgrade interface
        const rutDepth = cumulativePlasticStrain * params.thicknessOfSoftSubgrade;

        // F59: Critical rut depth (same as unstabilized)
        const criticalRutDepth = 0.025 * (1.5 + 0.125 * Math.log(totalPassages));

        // F60: Coefficient m
        const coefficientM = ((1 - Math.exp(-0.78 * (equivalentSquareSize / ballastThickness))) * rutDepth) / criticalRutDepth;

        // F61: Mobilized bearing capacity
        const mobilizedBearingCapacity = coefficientM * bearingCapacity;

        // F62: Pdyn, max calculated
        const pdynMaxCalculated =
            coefficientM *
            bearingCapacityFactor *
            cu *
            ((2 * equivalentBallastThickness * tanLoadSpreadingAngleFirst) / (0.85 * (1 + coefficientK2 * Math.log(totalPassages))) + equivalentSquareSize) ** 2;

        // F63: Pdyn - Pdyn calculated (goal seek target)
        const pdynDifference = maxDynamicWheelLoad - pdynMaxCalculated;

        // F64: Ballast thickness with subballast below
        const ballastThicknessWithSubballast =
            params.setBallastThicknessWithSubballast > 0 ? params.setBallastThicknessWithSubballast : ((100 - params.setPercentReductionBallastThickness) * ballastThickness) / 100;

        // F65: Subballast thickness
        const subballastThickness = (equivalentBallastThickness - ballastThicknessWithSubballast * chi1) / chi2;

        return {
            ballastThickness: Math.round(ballastThickness * 10000) / 10000,
            equivalentBallastThickness: Math.round(equivalentBallastThickness * 10000) / 10000,
            equivalentBallastThicknessWithSubballast: Math.round(equivalentBallastThicknessWithSubballast * 10000) / 10000,
            boussinesqR1: Math.round(boussinesqR1 * 10000) / 10000,
            boussinesqR2: Math.round(boussinesqR2 * 10000) / 10000,
            boussinesqR3: Math.round(boussinesqR3 * 10000) / 10000,
            stressRatio: Math.round(stressRatio * 10000) / 10000,
            tanLoadSpreadingAngleFirst: Math.round(tanLoadSpreadingAngleFirst * 10000) / 10000,
            coefficientK2: Math.round(coefficientK2 * 10000) / 10000,
            coefficientLambda2: Math.round(coefficientLambda2 * 10000) / 10000,
            tanLoadSpreadingAngleAfterN: Math.round(tanLoadSpreadingAngleAfterN * 10000) / 10000,
            bearingCapacityFactor: bearingCapacityFactor,
            bearingCapacity: Math.round(bearingCapacity * 10) / 10,
            ballastSubgradeContactArea: Math.round(ballastSubgradeContactArea * 10000) / 10000,
            pressureOnSubgrade: Math.round(pressureOnSubgrade * 100) / 100,
            pressureBearingCapacityRatio: Math.round(pressureBearingCapacityRatio * 10000) / 10000,
            cumulativePlasticStrain: Math.round(cumulativePlasticStrain * 1000000) / 1000000,
            rutDepth: Math.round(rutDepth * 10000) / 10000,
            criticalRutDepth: Math.round(criticalRutDepth * 10000) / 10000,
            coefficientM: Math.round(coefficientM * 10000) / 10000,
            mobilizedBearingCapacity: Math.round(mobilizedBearingCapacity * 100) / 100,
            pdynMaxCalculated: Math.round(pdynMaxCalculated * 100) / 100,
            pdynDifference: Math.round(pdynDifference * 1000000) / 1000000,
            ballastThicknessWithSubballast: Math.round(ballastThicknessWithSubballast * 1000) / 1000,
            subballastThickness: Math.round(subballastThickness * 10000) / 10000,
        };
    }

    /**
     * Helper method to get total wheel passages
     */
    private static getTotalWheelPassages(params: IRailwayInputParameters): number {
        const cargoTotalPassages = this.calculateTotalWheelPassages(
            params.selectedTrainType,
            'CARGO',
            params.cargoNumberOfTrainsPerDay,
            params.cargoNumberOfCarsPerTrain,
            params.cargoNumberOfAxlePerCar,
            params.cargoDesignLife
        );
        const passengerTotalPassages = this.calculateTotalWheelPassages(
            params.selectedTrainType,
            'PASSENGER',
            params.passengerNumberOfTrainsPerDay,
            params.passengerNumberOfCarsPerTrain,
            params.passengerNumberOfAxlePerCar,
            params.passengerDesignLife
        );
        return cargoTotalPassages + passengerTotalPassages;
    }

    /**
     * Helper method to get max dynamic wheel load
     */
    private static getMaxDynamicWheelLoad(params: IRailwayInputParameters): number {
        const cargoWheelLoad = this.calculateWheelLoad(params.selectedTrainType, 'CARGO', params.cargoDesignAxleLoad);
        const cargoDynamicAmplificationFactor = this.calculateDynamicAmplificationFactor(params.selectedTrainType, 'CARGO', params.cargoTrainSpeed, params.cargoWheelDiameter);
        const cargoDynamicWheelLoad = this.calculateDynamicWheelLoad(params.cargoNumberOfTrainsPerDay, cargoWheelLoad, cargoDynamicAmplificationFactor);

        const passengerWheelLoad = this.calculateWheelLoad(params.selectedTrainType, 'PASSENGER', params.passengerDesignAxleLoad);
        const passengerDynamicAmplificationFactor = this.calculateDynamicAmplificationFactor(params.selectedTrainType, 'PASSENGER', params.passengerTrainSpeed, params.passengerWheelDiameter);
        const passengerDynamicWheelLoad = this.calculateDynamicWheelLoad(params.passengerNumberOfTrainsPerDay, passengerWheelLoad, passengerDynamicAmplificationFactor);

        return Math.max(cargoDynamicWheelLoad, passengerDynamicWheelLoad);
    }

    /**
     * Helper method to get geogrid strength (T 2%)
     */
    private static getGeogridStrength(params: IRailwayInputParameters): number {
        return params.geogridStrength || this.DEFAULT_VALUES.GEOGRID_STRENGTH;
    }

    /**
     * Calculate cumulative plastic strain of subgrade based on soil type
     */
    private static calculateCumulativePlasticStrain(params: IRailwayInputParameters, totalPassages: number, pressureBearingCapacityRatio: number): number {
        const subgradeSoilType = params.subgradeSoilType || this.DEFAULT_VALUES.SUBGRADE_SOIL_TYPE;

        switch (subgradeSoilType) {
            case 'Fat Clay':
                return (1.2 / 100) * totalPassages ** 0.18 * pressureBearingCapacityRatio ** 2.4;
            case 'Lean Clay':
                return (1.1 / 100) * totalPassages ** 0.16 * pressureBearingCapacityRatio ** 1.8;
            case 'Elastic Silt':
                return (0.84 / 100) * totalPassages ** 0.13 * pressureBearingCapacityRatio ** 2;
            case 'Silt':
                return (0.64 / 100) * totalPassages ** 0.1 * pressureBearingCapacityRatio ** 1.7;
            default:
                return 0;
        }
    }

    /**
     * Calculate design summary results (final table)
     * Based on Excel formulas from analysis
     */
    private static calculateDesignSummary(
        unstabilizedCalculations: IRailwayUnstabilizedCalculations,
        stabilizedCalculations: IRailwayStabilizedCalculations,
        trainCalculations: IRailwayTrainCalculations
    ): IRailwayDesignSummary {
        // Unstabilized section calculations
        // Based on Excel formulas:
        // C73 = C41 (ballast thickness without subballast)
        // C74 = C73 * C36 / 100 (vertical deformation = thickness * plastic strain / 100)
        // C75 = C64 (ballast thickness with subballast below)
        // C76 = C65 (subballast thickness)
        // C77 = C75 * C36 / 100 (vertical deformation with subballast = thickness * plastic strain / 100)
        // C78 = C58 (rut depth at ballast-subgrade interface)

        const unstabilized = {
            ballastThicknessWithoutSubballast: unstabilizedCalculations.ballastThickness, // C73 = C41
            ballastVerticalDeformationWithoutSubballast: Math.round(((unstabilizedCalculations.ballastThickness * trainCalculations.totalPlasticStrainUnstabilised) / 100) * 1000) / 1000,
            ballastThicknessWithSubballastBelow: unstabilizedCalculations.ballastThicknessWithSubballast, // C75 = C64
            subballastThickness: unstabilizedCalculations.subballastThickness, // C76 = C65
            ballastVerticalDeformationWithSubballastBelow:
                Math.round(((unstabilizedCalculations.ballastThicknessWithSubballast * trainCalculations.totalPlasticStrainUnstabilised) / 100) * 1000) / 1000,
            rutDepthAtBallastSubgradeInterface: unstabilizedCalculations.rutDepth, // C78 = C58
        };

        // Stabilized section calculations (same formulas applied to stabilized values)
        const stabilized = {
            ballastThicknessWithoutSubballast: stabilizedCalculations.ballastThickness, // F73 = F41
            ballastVerticalDeformationWithoutSubballast: Math.round(((stabilizedCalculations.ballastThickness * trainCalculations.totalPlasticStrainStabilised) / 100) * 1000) / 1000,
            ballastThicknessWithSubballastBelow: stabilizedCalculations.ballastThicknessWithSubballast, // F75 = F64
            subballastThickness: stabilizedCalculations.subballastThickness, // F76 = F65
            ballastVerticalDeformationWithSubballastBelow: Math.round(((stabilizedCalculations.ballastThicknessWithSubballast * trainCalculations.totalPlasticStrainStabilised) / 100) * 1000) / 1000,
            rutDepthAtBallastSubgradeInterface: stabilizedCalculations.rutDepth, // F78 = F58
        };

        return {
            unstabilized,
            stabilized,
        };
    }
}
